import Grid from '@mui/material/Grid';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  findEconomicAccounitngReportRequested,
  reportEconomicAccountingReportLoadingSelector,
  reportFilterCountryCodeSelector,
  changeCollectionsEconomicAccountingFilterGroupBy,
  reportCollectionsFilterEconomicAccountingGroupBySelector,
  reportCollectionsFilterEconomicAccountingStartBusinessDateSelector,
  reportCollectionsFilterEconomicAccountingEndBusinessDateSelector,
} from '../state';

import {
  CountryFilter,
  PaymentTypesFilter,
  ReportActionsGridItem,
  ReportFiltersProps,
  ReportFilters,
  StoreFilter,
  FranchiseTypeFilterRedux,
} from './ReportFilters';

import { AppPermissions, ReportFilterEconomicAccountingGroupBy } from '../types';
import { EconomicAccountingBusinessDateRangeFilter } from './CollectionsFilters';
import { PermissionsProvider } from './Permissions';
import EconomicAccountingReportSwitch from './EconomicAccountingReportSwitch';
import EconomicAccountingReportFilterActions from './ReportFilters/EconomicAccountingReportFilterActions';
import { FilterPanel, StatusChip } from './Visual';

const groupByConfig: ReportFiltersProps<ReportFilterEconomicAccountingGroupBy>['groupByConfig'] = {
  options: {
    countryAcronym: true,
    businessDate: false,
    paymentTypeId: false,
    account: false,
    subaccount: false,
  },
  action: changeCollectionsEconomicAccountingFilterGroupBy,
  selector: reportCollectionsFilterEconomicAccountingGroupBySelector,
};

const onedriveAction = () => findEconomicAccounitngReportRequested(true);

const EconomicAccountingReportSearch = () => {
  const { t } = useTranslation();
  const startDate = useSelector(reportCollectionsFilterEconomicAccountingStartBusinessDateSelector);
  const endDate = useSelector(reportCollectionsFilterEconomicAccountingEndBusinessDateSelector);
  const hasCountry = Boolean(useSelector(reportFilterCountryCodeSelector));
  const applyDisabled = !(startDate.accept && endDate.accept && hasCountry);
  const countryRequiredIncomplete = applyDisabled && !hasCountry;
  const businessDateRequiredIncomplete = applyDisabled && !(startDate.accept && endDate.accept);
  const onedriveDisabled = applyDisabled;

  return (
    <PermissionsProvider resourcePermissions={[AppPermissions.SALES_TOTALS]}>
      <ReportFilters
        groupByConfig={groupByConfig}
        loadingSelector={reportEconomicAccountingReportLoadingSelector}
        applyAction={findEconomicAccounitngReportRequested}
        onedriveAction={onedriveAction}
      >
        <FilterPanel
          subtitleTranslationKey="economic-accounting-report.filters.subtitle"
          actions={
            <StatusChip
              status={applyDisabled ? 'warning' : 'success'}
              label={
                applyDisabled ? t('filters.status.missingRequired') : t('filters.status.ready')
              }
            />
          }
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3} lg={2} xl={2}>
              <CountryFilter fullWidth requiredIncomplete={countryRequiredIncomplete} />
            </Grid>
            <Grid item xs={12} md={3} lg={3} xl={2}>
              <StoreFilter />
            </Grid>
            <Grid item xs={12} md={6} lg={5} xl={4}>
              <EconomicAccountingBusinessDateRangeFilter
                spacing={1}
                requiredIncomplete={businessDateRequiredIncomplete}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <PaymentTypesFilter />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <EconomicAccountingReportSwitch />
            </Grid>
            <Grid item xs={12} md={4} lg={2} xl={2}>
              <FranchiseTypeFilterRedux fullWidth />
            </Grid>
            <ReportActionsGridItem>
              <EconomicAccountingReportFilterActions
                applyDisabled={applyDisabled}
                onedriveDisabled={onedriveDisabled}
              />
            </ReportActionsGridItem>
          </Grid>
        </FilterPanel>
      </ReportFilters>
    </PermissionsProvider>
  );
};

export default EconomicAccountingReportSearch;
